import * as Twilio from '@twilio/voice-sdk';
import React, {
    createContext,
    useContext,
    useReducer,
    useRef,
    useCallback,
    useEffect,
    useMemo,
} from 'react';

import { DossierId } from '../../resources/dossier/types';
import { PhoningMarkets } from '../../resources/phoning/types';

import { getPhoneCallsQueryKey } from '../../resources/phoning/queries';
import { useCreatePhoneCall } from '../../resources/phoning/mutations';
import { logPhoneCallError, isTwilioError } from '../../resources/phoning/logger';

import { queryClient } from './react-query-provider';

/**
 * Delay before refreshing phone call history after a call ends.
 * Allows backend time to process and save the call record.
 */
const PHONE_CALL_HISTORY_REFRESH_DELAY_MS = 3000;

/**
 * Delay before transitioning from ending to idle state.
 * Provides smooth UI transition when closing the modal.
 */
const CLEANUP_TRANSITION_DELAY_MS = 200;

/**
 * Phone Call State Machine
 *
 * States:
 * - idle: No modal, nothing happening
 * - preCall: Modal open, showing onboarding, waiting for user to start call
 * - starting: Fetching token + initializing Twilio device
 * - active: Call is in progress
 * - ending: Cleaning up resources (device destruction, query invalidation)
 * - error: Error occurred, user can retry or close
 *
 * Valid Transitions:
 * - idle → preCall (OPEN_MODAL: user opens call modal)
 * - preCall → starting (START_CALL: user initiates call after onboarding)
 * - preCall → idle (CLOSE_MODAL: user closes without calling)
 * - starting → active (CALL_CONNECTED: Twilio connection established, mic permission granted)
 * - starting → error (CALL_ERROR: token fetch, Twilio init, or mic permission failed)
 * - starting → ending (CLOSE_MODAL: user cancels, or DISCONNECTED: Twilio disconnect event)
 * - active → ending (HANG_UP: user ends call, or DISCONNECTED: Twilio disconnect event)
 * - ending → idle (CLEANUP_COMPLETE: resources cleaned up after normal call)
 * - ending → error (CALL_ERROR: error takes precedence if fired after disconnect)
 * - error → starting (START_CALL: user retries)
 * - error → idle (CLOSE_MODAL: user closes)
 *
 * See: documentation/tech/phoning-twilio-flow.md
 */
export type CallStatus = 'idle' | 'preCall' | 'starting' | 'active' | 'ending' | 'error';

export type PhoneCallState = {
    status: CallStatus;
    dossierId: DossierId | null;
    phoneCallId: number | null;
    phoneCallToken: string | null;
    requesterName: string | null;
    error: Twilio.TwilioError.TwilioError | null;
};

export type PhoneCallAction =
    | { type: 'OPEN_MODAL'; dossierId: DossierId }
    | { type: 'START_CALL'; dossierId: DossierId; requesterName: string }
    | { type: 'CALL_TOKEN_RECEIVED'; phoneCallToken: string; phoneCallId: number }
    | { type: 'CALL_CONNECTED' }
    | { type: 'CALL_ERROR'; error: Twilio.TwilioError.TwilioError }
    | { type: 'DISCONNECTED' }
    | { type: 'HANG_UP' }
    | { type: 'CLOSE_MODAL' }
    | { type: 'CLEANUP_COMPLETE' };

type PhoneCallContextValue = {
    state: PhoneCallState;
    device: Twilio.Device | null;
    call: Twilio.Call | null;
    openModal: (dossierId: DossierId) => void;
    startCall: (dossierId: DossierId, requesterName: string, language: PhoningMarkets) => void;
    hangUp: () => void;
    closeModal: () => void;
    isModalOpen: boolean;
    isCallActive: boolean;
};

const PhoneCallContext = createContext<PhoneCallContextValue | null>(null);

export const initialState: PhoneCallState = {
    status: 'idle',
    dossierId: null,
    phoneCallId: null,
    phoneCallToken: null,
    requesterName: null,
    error: null,
};

export function phoneCallReducer(state: PhoneCallState, action: PhoneCallAction): PhoneCallState {
    switch (action.type) {
        case 'OPEN_MODAL':
            // idle → preCall
            return {
                ...initialState,
                status: 'preCall',
                dossierId: action.dossierId,
            };

        case 'START_CALL':
            // preCall → starting, or error → starting (retry case)
            if (state.status !== 'preCall' && state.status !== 'error') {
                return state;
            }
            return {
                ...state,
                status: 'starting',
                dossierId: action.dossierId,
                requesterName: action.requesterName,
                phoneCallId: null,
                phoneCallToken: null,
                error: null,
            };

        case 'CALL_TOKEN_RECEIVED':
            // Stay in starting, just store token (triggers Twilio init via useEffect)
            if (state.status !== 'starting') {
                return state;
            }
            return {
                ...state,
                phoneCallToken: action.phoneCallToken,
                phoneCallId: action.phoneCallId,
            };

        case 'CALL_CONNECTED':
            // starting → active
            if (state.status !== 'starting') {
                return state;
            }
            return {
                ...state,
                status: 'active',
            };

        case 'CALL_ERROR':
            // starting → error, or ending → error (if HANG_UP won the race during error)
            // This ensures errors always take precedence over cleanup
            if (state.status !== 'starting' && state.status !== 'ending') {
                return state;
            }
            return {
                ...state,
                status: 'error',
                error: action.error,
            };

        case 'DISCONNECTED':
            // Twilio disconnect event: starting → ending, or active → ending
            // Can be overridden by CALL_ERROR (ending → error)
            if (state.status !== 'starting' && state.status !== 'active') {
                return state;
            }
            return {
                ...state,
                status: 'ending',
            };

        case 'HANG_UP':
            // User-initiated hang up: active → ending only
            if (state.status !== 'active') {
                return state;
            }
            return {
                ...state,
                status: 'ending',
            };

        case 'CLOSE_MODAL':
            // preCall → idle, starting → ending (user cancels), or error → idle
            if (state.status === 'preCall' || state.status === 'error') {
                return initialState;
            }
            if (state.status === 'starting') {
                // User cancelled during setup - transition to ending for cleanup
                return {
                    ...state,
                    status: 'ending',
                };
            }
            return state;

        case 'CLEANUP_COMPLETE':
            // ending → idle
            if (state.status !== 'ending') {
                return state;
            }
            return initialState;

        default:
            return state;
    }
}

export const PhoneCallProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(phoneCallReducer, initialState);
    const deviceRef = useRef<Twilio.Device | null>(null);
    const callRef = useRef<Twilio.Call | null>(null);

    const createCallMutation = useCreatePhoneCall();

    // Clean up device/call refs when starting a new call (e.g., after error retry)
    useEffect(() => {
        if (state.status === 'starting' && !state.phoneCallToken) {
            if (deviceRef.current) {
                deviceRef.current.disconnectAll();
                deviceRef.current.destroy();
                deviceRef.current = null;
            }
            callRef.current = null;
        }
    }, [state.status, state.phoneCallToken]);

    // Initialize Twilio device when token is received (still in 'starting' state)
    useEffect(() => {
        if (
            state.status === 'starting' &&
            state.phoneCallToken &&
            state.phoneCallId &&
            !deviceRef.current
        ) {
            const device = new Twilio.Device(state.phoneCallToken);
            device.updateOptions({
                codecPreferences: [Twilio.Call.Codec.Opus, Twilio.Call.Codec.PCMU],
                closeProtection: true,
            });

            device.on('error', (error: Twilio.TwilioError.TwilioError) => {
                logPhoneCallError('Device error', error, {
                    dossierId: state.dossierId,
                    phoneCallId: state.phoneCallId,
                });
                dispatch({ type: 'CALL_ERROR', error });
            });

            deviceRef.current = device;

            // Initiate call connection
            device
                .connect({
                    params: { id: state.phoneCallId.toString() },
                })
                .then((call) => {
                    callRef.current = call;

                    // Set up call event handlers
                    call.on('disconnect', () => {
                        dispatch({ type: 'DISCONNECTED' });
                    });

                    call.on('error', (error: Twilio.TwilioError.TwilioError) => {
                        logPhoneCallError('Call error', error, {
                            dossierId: state.dossierId,
                            phoneCallId: state.phoneCallId,
                        });
                        dispatch({ type: 'CALL_ERROR', error });
                    });

                    // Mark as connected only when the call is actually accepted
                    // (after microphone permission is granted and connection established)
                    call.on('accept', () => {
                        dispatch({ type: 'CALL_CONNECTED' });
                    });
                })
                .catch((error) => {
                    logPhoneCallError('Connection failed', error, {
                        dossierId: state.dossierId,
                        phoneCallId: state.phoneCallId,
                    });
                    dispatch({ type: 'CALL_ERROR', error });
                });
        }
    }, [state.status, state.phoneCallToken, state.phoneCallId, state.dossierId]);

    // Cleanup device when ending or error
    useEffect(() => {
        if (state.status === 'ending' || state.status === 'error') {
            // Disconnect call if still open
            if (callRef.current && callRef.current.status() === 'open') {
                callRef.current.disconnect();
            }

            // Clean up device
            if (deviceRef.current) {
                deviceRef.current.disconnectAll();
                deviceRef.current.destroy();
                deviceRef.current = null;
            }

            callRef.current = null;

            // Invalidate phone calls query to refresh the list
            if (state.dossierId) {
                setTimeout(() => {
                    void queryClient.invalidateQueries({
                        queryKey: getPhoneCallsQueryKey(state.dossierId!),
                    });
                }, PHONE_CALL_HISTORY_REFRESH_DELAY_MS);
            }

            // Only auto-close modal on normal ending (not on error)
            if (state.status === 'ending') {
                setTimeout(() => {
                    dispatch({ type: 'CLEANUP_COMPLETE' });
                }, CLEANUP_TRANSITION_DELAY_MS);
            }
        }
    }, [state.status, state.dossierId]);

    const openModal = useCallback((dossierId: DossierId) => {
        dispatch({ type: 'OPEN_MODAL', dossierId });
    }, []);

    const startCall = useCallback(
        (dossierId: DossierId, requesterName: string, language: PhoningMarkets) => {
            dispatch({ type: 'START_CALL', dossierId, requesterName });

            createCallMutation.mutate(
                { dossierId, language },
                {
                    onSuccess: (data) => {
                        dispatch({
                            type: 'CALL_TOKEN_RECEIVED',
                            phoneCallToken: data.token,
                            phoneCallId: data.phoneCallId,
                        });
                    },
                    onError: (error) => {
                        logPhoneCallError('Token fetch failed', error, {
                            dossierId,
                        });
                        // Convert non-Twilio errors to a generic Twilio error format
                        const twilioError: Twilio.TwilioError.TwilioError = isTwilioError(error)
                            ? error
                            : ({
                                  code: 0,
                                  message: error instanceof Error ? error.message : String(error),
                              } as Twilio.TwilioError.TwilioError);
                        dispatch({
                            type: 'CALL_ERROR',
                            error: twilioError,
                        });
                    },
                },
            );
        },
        [createCallMutation],
    );

    const hangUp = useCallback(() => {
        dispatch({ type: 'HANG_UP' });
    }, []);

    const closeModal = useCallback(() => {
        if (state.status === 'active') {
            // Active call - user hangs up
            dispatch({ type: 'HANG_UP' });
        } else {
            // preCall, starting, or error → close modal
            // CLOSE_MODAL handles starting → ending for cleanup
            dispatch({ type: 'CLOSE_MODAL' });
        }
    }, [state.status]);

    const isModalOpen = state.status !== 'idle';
    const isCallActive = state.status === 'active' || state.status === 'ending';

    const value: PhoneCallContextValue = useMemo(
        () => ({
            state,
            device: deviceRef.current,
            call: callRef.current,
            openModal,
            startCall,
            hangUp,
            closeModal,
            isModalOpen,
            isCallActive,
        }),
        [state, openModal, startCall, hangUp, closeModal, isModalOpen, isCallActive],
    );

    return <PhoneCallContext.Provider value={value}>{children}</PhoneCallContext.Provider>;
};

export const usePhoneCall = (): PhoneCallContextValue => {
    const context = useContext(PhoneCallContext);
    if (!context) {
        throw new Error('usePhoneCall must be used within a PhoneCallProvider');
    }
    return context;
};
